"use client";

import styled from "styled-components";

//Components
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from "../ui/alert-dialog";

export interface IconAlertProps {
  $color: "danger" | "warning" | "success";
}

export interface ConfirmButtonProps {
  $confirmWidth: boolean;
  $hasCancel: boolean;
}

const colorMap = {
  danger: {
    main: "var(--red)",
    light: "rgba(239, 68, 68, 0.1)",
    hover: "rgba(239, 68, 68, 0.9)",
  },
  warning: {
    main: "var(--orangeAndWhite)",
    light: "rgba(251, 146, 60, 0.1)",
    hover: "rgba(251, 146, 60, 0.9)",
  },
  success: {
    main: "var(--primarySystem100)",
    light: "rgba(34, 197, 94, 0.1)",
    hover: "rgba(34, 197, 94, 0.9)",
  },
};

export const ModalAlert = styled(AlertDialog)`
  z-index: 9999;
`;

export const ModalContent = styled(AlertDialogContent)`
  z-index: 99999;
  max-width: 420px;
  width: calc(100% - 2rem);
  padding: 0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

export const ContentWrapper = styled.div`
  padding: 32px 24px 24px 24px;
`;

export const IconContainer = styled.div<IconAlertProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background: ${({ $color }) => colorMap[$color].light};
  color: ${({ $color }) => colorMap[$color].main};
  flex-shrink: 0;

  svg {
    width: 32px;
    height: 32px;
    stroke-width: 2;
  }
`;

export const ModalTitle = styled(AlertDialogTitle)`
  font-family: "Inter SemiBold";
  font-size: 20px;
  line-height: 1.4;
  text-align: center;
  color: var(--titleColor);
  margin: 0 0 12px 0;
  padding: 0;

  @media (min-width: 768px) {
    font-size: 22px;
  }
`;

export const ModalDescription = styled(AlertDialogDescription)`
  font-family: "Roboto Regular";
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
  color: var(--textLight);
  margin: 0;
  padding: 0;
`;

export const FooterModal = styled(AlertDialogFooter)`
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0;
  margin: 0;
  border-top: 1px solid var(--borderColor);
  background: var(--backgroundColorCard);

  @media (min-width: 640px) {
    flex-direction: row;
    justify-content: stretch;
  }
`;

export const ButtonCancel = styled.button`
  font-family: "Roboto Medium";
  font-size: 14px;
  padding: 16px 20px;
  border-radius: 0;
  border: none;
  border-right: 1px solid var(--borderColor);
  background: var(--backgroundColorCard);
  color: var(--textColor);
  cursor: pointer;
  transition: all 0.2s ease;
  width: 50%;
  flex: 0 0 50%;

  &:hover {
    background: var(--lightGray);
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: var(--backgroundColorCard);
    transform: none;
  }
`;

export const ButtonAction = styled.button<IconAlertProps & ConfirmButtonProps>`
  font-family: "Roboto Medium";
  font-size: 14px;
  padding: 16px 24px;
  border-radius: 0;
  border: none;
  background: ${({ $color }) => colorMap[$color].main};
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  width: ${({ $hasCancel }) => ($hasCancel ? "50%" : "100%")};
  flex: ${({ $hasCancel }) => ($hasCancel ? "0 0 50%" : "1")};

  &:hover {
    background: ${({ $color }) => colorMap[$color].hover};
  }

  &:active {
    transform: scale(0.98);
  }

  &:focus {
    outline: none;
    box-shadow: inset 0 0 0 2px ${({ $color }) => colorMap[$color].light};
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    background: ${({ $color }) => colorMap[$color].main};
    transform: none;
    box-shadow: none;
  }
`;
